import * as Sentry from '@sentry/react';
import { isEmptyString, validateIsString } from '@vy/utils';
import type { Primitive } from '@vy/utils';
import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { isProduction } from './env-indicator';
import { getLocalStorageString } from './local-storage';

const SENSITIVE_HEADERS = new Set(['authorization', 'cookie', 'x-api-key', 'x-auth-token', 'x-access-token']);

export function initSentry() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: getSentryIntegrations(),
    beforeSend: handleBeforeSend,
    tracesSampleRate: 0.8,
    tracePropagationTargets: ['localhost', import.meta.env.VITE_WEFT_API].filter(Boolean),
    beforeBreadcrumb: handleBeforeBreadcrumb,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

function scrubHeaders(headers: Record<string, string>): void {
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
      headers[key] = '[Filtered]';
    }
  }
}

function handleBeforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (isUnauthorizedUser()) {
    return null;
  }

  if (event.request?.headers) {
    scrubHeaders(event.request.headers as Record<string, string>);
  }

  const userEmail = event?.tags?.['user.email'];
  if (isProduction() && isDeveloper(userEmail)) {
    event.level = 'debug';
  }

  return event;
}

function handleBeforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
    return null;
  }
  if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
    const url: string = breadcrumb.data?.url ?? '';
    if (url.includes('/health') || url.includes('/metrics')) {
      return null;
    }
    if (breadcrumb.data?.['request_headers']) {
      scrubHeaders(breadcrumb.data['request_headers'] as Record<string, string>);
    }
  }
  return breadcrumb;
}

function isUnauthorizedUser() {
  const token = getLocalStorageString('token');

  return isEmptyString(token);
}

function isDeveloper(email: Primitive) {
  validateIsString(email);
  const developerEmailDomains = ['@withvayu.com', '@goweft.com'];

  return developerEmailDomains.some((domain) => email.endsWith(domain));
}

function getSentryIntegrations() {
  const commonIntegrations = [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.extraErrorDataIntegration(),
  ];

  return isProduction() ? [...commonIntegrations, Sentry.replayIntegration()] : commonIntegrations;
}
